/** Agenda Lohnbuchungen CSV export (Zulage, ÜN-Pauschale, VMA, Be-/Abzüge). */

import { calcWorkHours, isWithinEmployment } from './employeeTimeEntryHours';
import { formatPayrollHours, roundPayrollExportHours } from './employeePayrollExport';
import { derivePayrollSummary, roundPayrollMoney, type PayrollHoursByType } from './payrollSummaryCalc';

export const PAYROLL_BOOKINGS_CSV_HEADERS = [
  'Personalnr.',
  'Lohnart',
  'Lohnsatz',
  'Wert',
  'Kost1',
  'Kost2',
  'Monat',
  'Jahr',
] as const;

export const LOHNART_ZULAGE = 1000;
export const LOHNART_UEN = 1001;
export const LOHNART_VMA = 1002;

export type PayrollBookingsTimeEntry = {
  entry_date: string;
  entry_type: string;
  start_time: string | null;
  end_time: string | null;
  break_minutes?: number | null;
};

export type PayrollBookingsEmployeeInput = {
  personal_number: string;
  last_name: string;
  first_name: string;
  allowance_per_hour: number;
  vma_tax_free_per_day: number;
  overnight_tax_free_per_day: number;
  /** Zulagenstunden für Lohnart 1000: Arbeit + Lohnfortzahlung + Urlaub + Feiertag. */
  billable_hours: number;
  work_days: number;
};

export type PayrollBookingsRow = {
  personalNr: string;
  lohnart: number;
  wert: string;
  monat: number;
  jahr: number;
  lastName: string;
  firstName: string;
};

/** Count distinct calendar days with positive work hours. */
export function countWorkDaysWithHours(
  entries: PayrollBookingsTimeEntry[],
  employeeStartDate: string | null | undefined,
  employeeEndDate?: string | null | undefined,
): number {
  const dates = new Set<string>();
  for (const entry of entries) {
    if (entry.entry_type !== 'work') continue;
    if (!isWithinEmployment(entry.entry_date, employeeStartDate, employeeEndDate)) continue;
    const hours = calcWorkHours(entry.start_time, entry.end_time, entry.break_minutes);
    if (hours > 0) dates.add(entry.entry_date.slice(0, 10));
  }
  return dates.size;
}

export function parseBookingsMonthYear(toYmd: string): { month: number; year: number } | null {
  const normalized = String(toYmd ?? '').trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(normalized)) return null;
  const year = Number.parseInt(normalized.slice(0, 4), 10);
  const month = Number.parseInt(normalized.slice(5, 7), 10);
  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) return null;
  return { month, year };
}

export function formatBookingsHours(value: number): string {
  return formatPayrollHours(value);
}

export function formatBookingsDays(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return '';
  return String(Math.floor(value));
}

export function formatBookingsAmount(value: number): string {
  const rounded = roundPayrollMoney(value);
  if (!Number.isFinite(rounded) || rounded <= 0) return '';
  const [whole, frac = ''] = rounded.toFixed(2).split('.');
  return `${whole},${frac}`;
}

export function buildPayrollBookingsRowsForEmployee(
  emp: PayrollBookingsEmployeeInput,
  month: number,
  year: number,
): PayrollBookingsRow[] {
  const rows: PayrollBookingsRow[] = [];
  const base = {
    personalNr: emp.personal_number,
    monat: month,
    jahr: year,
    lastName: emp.last_name,
    firstName: emp.first_name,
  };

  if (emp.allowance_per_hour > 0 && emp.billable_hours > 0) {
    const wert = formatBookingsHours(emp.billable_hours);
    if (wert) rows.push({ ...base, lohnart: LOHNART_ZULAGE, wert });
  }

  if (emp.vma_tax_free_per_day > 0 && emp.work_days > 0) {
    const wert = formatBookingsDays(emp.work_days);
    if (wert) rows.push({ ...base, lohnart: LOHNART_VMA, wert });
  }

  if (emp.overnight_tax_free_per_day > 0 && emp.work_days > 0) {
    const amount = roundPayrollMoney(emp.work_days * emp.overnight_tax_free_per_day);
    const wert = formatBookingsAmount(amount);
    if (wert) rows.push({ ...base, lohnart: LOHNART_UEN, wert });
  }

  return rows;
}

export function sortPayrollBookingsRows(rows: PayrollBookingsRow[]): PayrollBookingsRow[] {
  return [...rows].sort((a, b) => {
    const lastCmp = a.lastName.localeCompare(b.lastName, 'de');
    if (lastCmp !== 0) return lastCmp;
    const firstCmp = a.firstName.localeCompare(b.firstName, 'de');
    if (firstCmp !== 0) return firstCmp;
    return a.lohnart - b.lohnart;
  });
}

export function roundBookingsNetWorkHours(workHours: number, overtimeCompHours: number): number {
  return roundPayrollExportHours(Math.max(0, workHours - overtimeCompHours));
}

export function roundBookingsBillableHours(hours: PayrollHoursByType): number {
  return derivePayrollSummary(hours, 0, 0).billable_hours;
}

export function roundBookingsAllowanceHours(hours: PayrollHoursByType): number {
  return roundPayrollExportHours(hours.work + hours.vacation + hours.sickness + hours.public_holiday);
}

export type PayrollAllowanceBreakdown = {
  allowance_hours: number;
  work_days: number;
  allowance_hourly_pay: number;
  vma_pay: number;
  overnight_pay: number;
  total_allowance_pay: number;
};

/** Zulagen aus Zulagenstunden und Arbeitstagen (Lohnbuchhaltung-Vorschau). */
export function derivePayrollAllowances(params: {
  allowance_hours: number;
  work_days: number;
  allowance_per_hour: number;
  vma_tax_free_per_day: number;
  overnight_tax_free_per_day: number;
}): PayrollAllowanceBreakdown {
  const workDays = Math.max(0, Math.floor(params.work_days));
  const allowanceHourlyPay =
    params.allowance_per_hour > 0 && params.allowance_hours > 0
      ? roundPayrollMoney(params.allowance_hours * params.allowance_per_hour)
      : 0;
  const vmaPay =
    params.vma_tax_free_per_day > 0 && workDays > 0
      ? roundPayrollMoney(workDays * params.vma_tax_free_per_day)
      : 0;
  const overnightPay =
    params.overnight_tax_free_per_day > 0 && workDays > 0
      ? roundPayrollMoney(workDays * params.overnight_tax_free_per_day)
      : 0;
  return {
    allowance_hours: roundPayrollExportHours(params.allowance_hours),
    work_days: workDays,
    allowance_hourly_pay: allowanceHourlyPay,
    vma_pay: vmaPay,
    overnight_pay: overnightPay,
    total_allowance_pay: roundPayrollMoney(allowanceHourlyPay + vmaPay + overnightPay),
  };
}
